import { RefObject, useCallback, useEffect, useEffectEvent } from 'react';

import {
	ContentType,
	MediaEl,
	MediaPlayerOverrides,
	OnPlay,
	PlayerMode,
	TransportClickHandler,
} from '@/components/app/contentPlayer/MediaPlayerProvider/types';
import { useAnalytics } from '@/hooks/analytics';
import { Guide } from '@/store/guide/types';
import { Program } from '@/store/program/types';
import { formatResizedUrl } from '@/utils/app/formatResizedUrl';
import { calmLogger } from '@/utils/calmLogger';
import { getCurrentTime, isAudioMediaEl, isVideoMediaEl, setCurrentTime, toggleMuted } from '@/utils/media';

export const SKIP_SECONDS = 15;
export const PREVIEW_DURATION_SECONDS = 30;

export type ShortcutAction =
	| 'play'
	| 'pause'
	| 'seek_backward'
	| 'seek_forward'
	| 'restart'
	| 'seek_percent'
	| 'toggle_mute'
	| 'toggle_fullscreen'
	| 'seek_to'
	| 'close'
	| 'stop'
	| 'show_help';
export type ShortcutInput = 'keyboard' | 'media_key';

const NON_TEXT_INPUT_TYPES = [
	'button',
	'checkbox',
	'color',
	'file',
	'image',
	'radio',
	'range',
	'reset',
	'submit',
];

const isTextEntryTarget = (el: HTMLElement): boolean => {
	if (
		el.isContentEditable ||
		el.closest('textarea, select, [contenteditable=""], [contenteditable="true"]')
	) {
		return true;
	}
	const input = el.closest('input');
	return Boolean(input && !NON_TEXT_INPUT_TYPES.includes(input.type));
};

// Every input but a range slider, and these elements, already act on Space
const isSpaceActivatedTarget = (el: HTMLElement): boolean =>
	Boolean(el.closest('button, [role="button"], summary, input:not([type="range"])'));

// Menus, listboxes and open popups own their arrows, Home and Esc.
// Dialogs are left out because the video overlay is one, and modal dialogs are caught by aria-modal.
const isPopupTarget = (el: HTMLElement): boolean =>
	Boolean(
		el.closest(
			'[role="menu"], [role="menubar"], [role="listbox"], [role="combobox"], [role="radiogroup"], [aria-expanded="true"]',
		),
	);

const isArrowKeyTarget = (el: HTMLElement): boolean =>
	Boolean(el.closest('[role="slider"], [role="radio"], input[type="range"], input[type="radio"]'));

const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'Home'];

interface Binding {
	run: () => Promise<ShortcutAction | null> | ShortcutAction | null;
	repeatable?: boolean;
}

const MEDIA_SESSION_ACTIONS: MediaSessionAction[] = [
	'play',
	'pause',
	'stop',
	'seekbackward',
	'seekforward',
	'seekto',
	'previoustrack',
	'nexttrack',
];
const ARTWORK_SIZE = 512;

const isFullscreen = (mediaEl: MediaEl): boolean =>
	Boolean(document.fullscreenElement) || (isVideoMediaEl(mediaEl) && mediaEl.isFullscreen());

// A play() cut short by a pause, or blocked by autoplay rules, is an expected outcome rather than a fault
const isExpectedPlaybackError = (error: unknown): error is DOMException =>
	error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError');

const logShortcutError = (message: string, context: Record<string, string | null>, error: unknown) => {
	if (isExpectedPlaybackError(error)) {
		calmLogger.warn(message, { ...context, error: error.message });
		return;
	}
	calmLogger.error(message, context, error);
};

const getMediaSession = (): MediaSession | null =>
	typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : null;

interface UseMediaControlsArgs {
	mediaElRef: RefObject<MediaEl>;
	contentType: ContentType;
	playerMode: PlayerMode;
	isPreviewMode: boolean;
	overrides: MediaPlayerOverrides;
	setTrackProgress: (progress: number) => void;
	onPlay: OnPlay;
	onPause: TransportClickHandler;
	onStop: () => Promise<void>;
	trackProgress: number;
	onShowShortcutHelp: () => void;
	guide?: Guide | null;
	program?: Program | null;
	fallbackDuration?: number;
}

export const useMediaControls = ({
	mediaElRef,
	contentType,
	playerMode,
	isPreviewMode,
	overrides,
	setTrackProgress,
	onPlay,
	onPause,
	onStop,
	trackProgress,
	onShowShortcutHelp,
	guide,
	program,
	fallbackDuration,
}: UseMediaControlsArgs) => {
	const { logEvent } = useAnalytics();

	// Audio reports NaN until its metadata loads, so fall back to the guide's asset duration.
	// Returns 0 when the length is still unknown.
	const getDuration = useCallback((): number => {
		const el = mediaElRef.current;
		let duration = NaN;
		if (isAudioMediaEl(el)) {
			duration = el.duration;
		} else if (isVideoMediaEl(el)) {
			duration = el.duration();
		}
		if (!Number.isFinite(duration) || duration <= 0) {
			duration = fallbackDuration !== undefined && Number.isFinite(fallbackDuration) ? fallbackDuration : 0;
		}
		if (isPreviewMode) {
			return duration > 0 ? Math.min(duration, PREVIEW_DURATION_SECONDS) : PREVIEW_DURATION_SECONDS;
		}
		return duration;
	}, [mediaElRef, fallbackDuration, isPreviewMode]);

	const seekTo = useCallback(
		(time: number) => {
			const el = mediaElRef.current;
			if (!el) return;

			const duration = getDuration();
			let target = Math.max(0, time);
			if (duration > 0) {
				target = Math.min(target, duration);
			}

			if (overrides.seekTo) {
				overrides.seekTo(target);
				return;
			}
			setCurrentTime(el, target);
			setTrackProgress(target);
		},
		[mediaElRef, getDuration, overrides, setTrackProgress],
	);

	const seekBy = useCallback(
		(deltaSeconds: number) => {
			seekTo(getCurrentTime(mediaElRef.current, contentType) + deltaSeconds);
		},
		[mediaElRef, contentType, seekTo],
	);

	const toggleMute = useCallback(() => toggleMuted(mediaElRef.current), [mediaElRef]);

	const toggleFullscreen = useCallback(async () => {
		const el = mediaElRef.current;
		if (!isVideoMediaEl(el)) return;

		if (el.isFullscreen()) {
			await el.exitFullscreen();
		} else {
			await el.requestFullscreen();
		}
	}, [mediaElRef]);

	// The provider's video.js play/pause listeners already sync player state and log the session,
	// so video goes straight to the player rather than through onPlay/onPause.
	const play = useCallback(async () => {
		const el = mediaElRef.current;
		if (isVideoMediaEl(el)) {
			await el.play();
		} else if (playerMode !== 'playing') {
			await onPlay();
		}
	}, [mediaElRef, playerMode, onPlay]);

	const pause = useCallback(() => {
		const el = mediaElRef.current;
		if (isVideoMediaEl(el)) {
			el.pause();
		} else if (playerMode === 'playing') {
			onPause();
		}
	}, [mediaElRef, playerMode, onPause]);

	// Returns the action taken so callers can log whether this was a play or a pause
	const togglePlay = useCallback(async (): Promise<ShortcutAction> => {
		const el = mediaElRef.current;
		const isPlaying = isVideoMediaEl(el) ? !el.paused() : playerMode === 'playing';
		if (isPlaying) {
			pause();
			return 'pause';
		}
		await play();
		return 'play';
	}, [mediaElRef, playerMode, play, pause]);

	const stop = useCallback(() => onStop(), [onStop]);

	const logShortcut = useCallback(
		(action: ShortcutAction, input: ShortcutInput) => {
			logEvent({ eventName: 'Player : Shortcut : Used', eventProps: { action, input } });
		},
		[logEvent],
	);

	const isActive = playerMode !== 'stopped' && (contentType === 'audio' || contentType === 'video');

	// Listeners read the latest callbacks through the effect event, so they attach once per active period
	const handleShortcutKeyDown = useEffectEvent((e: KeyboardEvent) => {
		const skipBack: Binding = {
			repeatable: true,
			run: () => {
				seekBy(-SKIP_SECONDS);
				return 'seek_backward';
			},
		};
		const skipForward: Binding = {
			repeatable: true,
			run: () => {
				seekBy(SKIP_SECONDS);
				return 'seek_forward';
			},
		};
		const playPause: Binding = { run: togglePlay };

		const getMediaKeyBinding = (key: string): Binding | null => {
			switch (key) {
				case 'MediaPlayPause':
					return playPause;
				case 'MediaTrackPrevious':
					return skipBack;
				case 'MediaTrackNext':
					return skipForward;
				case 'MediaStop':
					return {
						run: async (): Promise<ShortcutAction> => {
							await stop();
							return 'stop';
						},
					};
				default:
					return null;
			}
		};

		const getShortcutBinding = (key: string): Binding | null => {
			switch (key) {
				case ' ':
				case 'k':
					return playPause;
				case 'ArrowLeft':
				case 'j':
					return skipBack;
				case 'ArrowRight':
				case 'l':
					return skipForward;
				case 'Home':
				case '0':
					return {
						run: () => {
							seekTo(0);
							return 'restart';
						},
					};
				case 'm':
					return {
						run: () => {
							toggleMute();
							return 'toggle_mute';
						},
					};
				case 'f':
					if (!isVideoMediaEl(mediaElRef.current)) return null;
					return {
						run: async (): Promise<ShortcutAction> => {
							await toggleFullscreen();
							return 'toggle_fullscreen';
						},
					};
				case 'Escape':
					// Esc belongs to the browser while fullscreen, and exiting fullscreen must not end the session
					if (isFullscreen(mediaElRef.current)) return null;
					return {
						run: async (): Promise<ShortcutAction> => {
							await stop();
							return 'close';
						},
					};
				case '?':
					// The help modal renders outside the fullscreen element, so it would open unseen and trap the keys
					if (isFullscreen(mediaElRef.current)) return null;
					return {
						run: () => {
							onShowShortcutHelp();
							return 'show_help';
						},
					};
				default:
					if (!/^[1-9]$/.test(key)) return null;
					return {
						run: () => {
							const duration = getDuration();
							if (duration <= 0) return null;
							seekTo((duration * Number(key)) / 10);
							return 'seek_percent';
						},
					};
			}
		};

		const getBinding = (e: KeyboardEvent): { binding: Binding; input: ShortcutInput } | null => {
			const mediaKeyBinding = getMediaKeyBinding(e.key);
			if (mediaKeyBinding) {
				// Browsers with Media Session route these keys to its handlers, so handling both would act twice
				if (getMediaSession()) return null;
				return { binding: mediaKeyBinding, input: 'media_key' };
			}

			const target = e.target instanceof HTMLElement ? e.target : null;
			if (target && (isTextEntryTarget(target) || isPopupTarget(target))) return null;

			const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
			// A focused control already activates on Space, and a focused slider or radio already moves on arrows
			if (key === ' ' && target && isSpaceActivatedTarget(target)) return null;
			if (ARROW_KEYS.includes(key) && target && isArrowKeyTarget(target)) return null;

			const binding = getShortcutBinding(key);
			return binding ? { binding, input: 'keyboard' } : null;
		};

		if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || !mediaElRef.current) return;
		// Another dialog owns the keyboard, and its Esc should close it rather than the player
		if (document.querySelector('[aria-modal="true"]')) return;

		const match = getBinding(e);
		if (!match) return;

		// Stops Space scrolling the page, and stops Safari firing a second toggle
		e.preventDefault();
		if (e.repeat && !match.binding.repeatable) return;

		// Deferring the call lets a synchronous throw reach the catch instead of escaping the listener
		Promise.resolve()
			.then(() => match.binding.run())
			.then(action => {
				if (action) logShortcut(action, match.input);
			})
			.catch(error =>
				logShortcutError(
					'Error in useMediaControls keydown',
					{ key: e.key, input: match.input, contentType },
					error,
				),
			);
	});

	useEffect(() => {
		if (!isActive) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			handleShortcutKeyDown(e);
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isActive]);

	const runMediaSessionAction = useEffectEvent(
		(action: MediaSessionAction, details: MediaSessionActionDetails) => {
			const commands: Partial<Record<MediaSessionAction, [ShortcutAction, () => unknown]>> = {
				play: ['play', play],
				pause: ['pause', pause],
				stop: ['stop', stop],
				seekbackward: ['seek_backward', () => seekBy(-(details.seekOffset ?? SKIP_SECONDS))],
				seekforward: ['seek_forward', () => seekBy(details.seekOffset ?? SKIP_SECONDS)],
				seekto: ['seek_to', () => seekTo(details.seekTime ?? 0)],
				// There is no queue, so track keys skip within the session the way podcast apps do
				previoustrack: ['seek_backward', () => seekBy(-SKIP_SECONDS)],
				nexttrack: ['seek_forward', () => seekBy(SKIP_SECONDS)],
			};
			const command = commands[action];
			if (!command || (action === 'seekto' && details.seekTime === undefined)) return;

			const [shortcut, fn] = command;
			Promise.resolve()
				.then(fn)
				.then(() => logShortcut(shortcut, 'media_key'))
				.catch(error =>
					logShortcutError('Error in useMediaControls media session', { action, contentType }, error),
				);
		},
	);

	// Handlers read the latest callbacks through the effect event, so they register once per active period
	useEffect(() => {
		const mediaSession = getMediaSession();
		if (!isActive || !mediaSession) return;

		const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
			try {
				mediaSession.setActionHandler(action, handler);
			} catch {
				// Browsers throw for actions they don't support
			}
		};

		MEDIA_SESSION_ACTIONS.forEach(action =>
			setHandler(action, details => runMediaSessionAction(action, details)),
		);
		return () => {
			MEDIA_SESSION_ACTIONS.forEach(action => setHandler(action, null));
		};
	}, [isActive]);

	useEffect(() => {
		const mediaSession = getMediaSession();
		if (!isActive || !mediaSession || !guide || typeof MediaMetadata === 'undefined') return;

		const artworkUrl = program?.background_image?.url ?? guide.cell_image?.url;
		mediaSession.metadata = new MediaMetadata({
			title: guide.title,
			artist: guide.narrator_name ?? '',
			album: program?.title && program.title !== guide.title ? program.title : '',
			artwork: artworkUrl
				? [
						{
							src: formatResizedUrl(artworkUrl, ARTWORK_SIZE, ARTWORK_SIZE),
							sizes: `${ARTWORK_SIZE}x${ARTWORK_SIZE}`,
						},
					]
				: [],
		});
		return () => {
			mediaSession.metadata = null;
		};
	}, [isActive, guide, program]);

	useEffect(() => {
		const mediaSession = getMediaSession();
		if (!isActive || !mediaSession) return;

		mediaSession.playbackState = playerMode === 'playing' ? 'playing' : 'paused';
		return () => {
			mediaSession.playbackState = 'none';
		};
	}, [isActive, playerMode]);

	useEffect(() => {
		const mediaSession = getMediaSession();
		if (!isActive || !mediaSession?.setPositionState) return;

		const duration = getDuration();
		if (duration <= 0) return;

		try {
			// Position is clamped because it throws past the duration, which preview mode can briefly reach
			mediaSession.setPositionState({
				duration,
				playbackRate: 1,
				position: Math.min(Math.max(0, trackProgress), duration),
			});
		} catch {
			// This runs every second, so a browser that rejects the state shouldn't flood the logs
		}
	}, [isActive, trackProgress, getDuration]);

	useEffect(() => {
		const mediaSession = getMediaSession();
		if (!isActive || !mediaSession?.setPositionState) return;

		return () => {
			try {
				mediaSession.setPositionState();
			} catch {
				// Some browsers reject clearing the position state
			}
		};
	}, [isActive]);

	return { getDuration, seekTo, seekBy, toggleMute, toggleFullscreen, togglePlay };
};
